import { useEffect, useState } from "react";
import { AppShell } from "../app/AppShell";
import { Button } from "../components/Button";
import { HELP_SECTIONS, helpBody, helpTitle } from "../components/Help";
import { PageHead } from "../components/Page";
import { Chip } from "../components/Status";
import { useMe } from "../app/AuthGate";
import { useRouteParam } from "../lib/route";
import { tx } from "../i18n";
import "./HelpPage.css";

/* ══ 도움말(#/help) — t-in-port-help ══
   연결 방식 둘(B · MCP 추천, A · 복붙)과 화면별 절. `?s=<절 id>` 로 오면 그 절로 굴리고 표시한다 — 제목줄 (?) 판의
   「도움말에서 더 보기」가 그 주소를 연다. 원본 도움말에 붙어 있던 「내 차례」는 셸의 내 차례 서랍이 맡는다. */

function CopyLine({ text }: { text: string }) {
  const [done, setDone] = useState<"ok" | "fail" | null>(null);
  return (
    <div className="help-copy">
      <code>{text}</code>
      <Button
        size="sm"
        onClick={() =>
          navigator.clipboard.writeText(text).then(
            () => setDone("ok"),
            () => setDone("fail"),
          )
        }
      >
        {tx("common.action.copy")}
      </Button>
      {done ? <span className="caption" role="status">{tx(done === "ok" ? "runners.token.copied" : "runners.token.copyFailed")}</span> : null}
    </div>
  );
}

export function HelpPage() {
  const [tab, setTab] = useState<"b" | "a">("b");
  const s = useRouteParam("s");
  const me = useMe();
  // 못 읽은 동안은 관리자 절도 보인다 — 숨겨서 「없는 화면」처럼 보이게 하지 않는다(누르면 그 화면이 403 을 말한다).
  const isAdmin = !me || me.me.role === "admin";
  const mcp = `${location.origin}/mcp`;

  useEffect(() => {
    if (s) document.getElementById(`help-${s}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [s]);

  const secs = HELP_SECTIONS.filter((x) => !x.admin || isAdmin);
  return (
    <AppShell route="/help">
      <PageHead title={tx("nav.help")} lead={<p>{tx("help.lead")}</p>} />
      <section className="b-card help-card" aria-labelledby="help-connect">
        <h2 id="help-connect" className="b-card-title">{tx("help.connect")}</h2>
        <div className="b-tabs" role="tablist" aria-label={tx("help.tabs")}>
          <button type="button" role="tab" aria-selected={tab === "b"} className={`b-tab${tab === "b" ? " is-on" : ""}`} onClick={() => setTab("b")}>
            {tx("help.tabB")} <Chip tone="success">{tx("help.recommended")}</Chip>
          </button>
          <button type="button" role="tab" aria-selected={tab === "a"} className={`b-tab${tab === "a" ? " is-on" : ""}`} onClick={() => setTab("a")}>
            {tx("help.tabA")}
          </button>
        </div>
        {tab === "b" ? (
          <div role="tabpanel" className="help-panel">
            <p className="muted">{tx("help.b.lead")}</p>
            <ol className="help-steps">
              <li>
                {tx("help.b.step1")}
                <CopyLine text={mcp} />
                <span className="caption">{tx("help.b.claude")}</span>
                <CopyLine text={`claude mcp add --transport http b3x-in ${mcp}`} />
              </li>
              <li>{tx("help.b.step2")}</li>
              <li>{tx("help.b.step3")}</li>
            </ol>
            {location.protocol === "https:" ? <p className="caption">{tx("help.b.https")}</p> : null}
          </div>
        ) : (
          <div role="tabpanel" className="help-panel">
            <p className="muted">{tx("help.a.lead")}</p>
            <ol className="help-steps">
              <li>{tx("help.a.step1")}</li>
              <li>{tx("help.a.step2")}</li>
              <li>{tx("help.a.step3")}</li>
            </ol>
          </div>
        )}
      </section>

      <h2 className="help-h">{tx("help.toc")}</h2>
      <nav className="help-toc" aria-label={tx("help.toc")}>
        {secs.map((x) => (
          <a key={x.id} className={`b-chip${s === x.id ? " is-on" : ""}`} href={`#/help?s=${x.id}`}>
            {helpTitle(x)}
          </a>
        ))}
      </nav>
      <div className="help-secs">
        {secs.map((x) => (
          <section key={x.id} id={`help-${x.id}`} className={`b-card help-sec${s === x.id ? " is-on" : ""}`} aria-labelledby={`help-${x.id}-t`}>
            <h3 id={`help-${x.id}-t`} className="b-card-title">{helpTitle(x)}</h3>
            <p>{helpBody(x)}</p>
            <a href={`#${x.route}`}>{tx("help.open")} →</a>
          </section>
        ))}
      </div>
    </AppShell>
  );
}
